import {ApplicationRef, createComponent, Injectable} from "@angular/core";
import { DynamicComponentService } from '@core/service/dynamic-component.service';
import { ClusterInfoPopupComponent } from '@shared/cluster-info-popup/cluster-info-popup.component';
import { LabelPopupComponent } from '@shared/label-form-popup/label-form-popup.component';
import {DatacenterV2} from "./model/datacenter-v2";
import mapboxgl, {Popup} from "mapbox-gl";
import {Actions} from "@ngrx/effects";
import {
  datacenterActions
} from 'src/app/core/ngrx/actions';
import {Store} from "@ngrx/store";
import {InfoPopupComponent} from "../shared/info-popup/info-popup.component";
import {DataCentersService} from "./service/data-centers.service";
import {MARKERS_SOURCE_ID} from "./consts/map.constants";
import {MapConfigSelector} from "./ngrx/selectors/map-config.selector";
import {AvailabilityZoneHandler} from "./service/availability-zone.handler";

@Injectable()
export class DcMouseEventsHandler {
  private areMarkersBlocked = false;
  private markersSourceId = 'datacenter-markers';
  private activePopup: mapboxgl.Popup | null = null;

  constructor(private actions$: Actions, private readonly store: Store, private applicationRef: ApplicationRef,
              private datacentersService: DataCentersService, mapConfigSelector: MapConfigSelector,
              private availabilityZoneHandler: AvailabilityZoneHandler, private dynamicComponentService: DynamicComponentService) {
    // Listen to actions that block markers
    mapConfigSelector.areMarkersBlocked().subscribe(blocked => {
      this.areMarkersBlocked = blocked;
    });

  }

  public onClick(e: any, map: mapboxgl.Map) {
    e.preventDefault();
    const coordinates = e.lngLat;
    const feature = e.features[0];
    if (feature) {
      try {
        if (feature.properties.isCluster) {
          this.createClusterInfoPopup(coordinates, feature.properties as DatacenterV2, map);
        } else {
          this.createInfoPopup(coordinates, this.datacentersService.getDatacenterFromFeature(feature), map);
        }
      } catch (error) {
        console.error('Error parsing datacenter in onClick:', error);
      }
    }
  }

  public onMarkerMouseDown = (e: any, map: mapboxgl.Map) => {
    if (this.areMarkersBlocked) return;
    e.preventDefault();
    const feature = e.features[0];
    if (feature) {
      try {
        let datacenter: DatacenterV2 = {...feature.properties};
        let customMarkerDragListener = (event: any) => {
          this.onMarkerDrag(event, map, datacenter);
        }
        let customMarkerDragEndListener = (event: any) => {
          this.onMarkerDragEnd(event, map, datacenter, customMarkerDragListener, customMarkerDragEndListener);
        }
        map.on('mousemove', customMarkerDragListener);
        map.on('mouseup', customMarkerDragEndListener);

        map.dragPan.disable();
      } catch (error) {
        console.error('Error parsing datacenter in onMarkerMouseDown:', error);
      }
    }
  };

  private onMarkerDrag = (e: any, map: mapboxgl.Map, datacenter: DatacenterV2) => {
    window.requestAnimationFrame(() => {
      const coords = e.lngLat;
      map.getCanvasContainer().style.cursor = 'grabbing';
      const source = map.getSource(this.markersSourceId) as any;
      if (!source || !source._data) {
        console.warn('DCMouseEventsHandler: Source or data not available');
        return;
      }
      const geojson = source._data;
      let featureToUpdate = null;
      if (datacenter.auxId) {
        featureToUpdate = geojson.features.find(f => f.properties["auxId"] === datacenter.auxId);
      } else {
        featureToUpdate = geojson.features.find(f => f.properties["id"] === datacenter.id);
      }
      if (featureToUpdate) {
        featureToUpdate.geometry.coordinates = [coords.lng, coords.lat];
        featureToUpdate.properties["isDragging"] = true;
        source.setData(geojson);
      }
    });
  };

  private onMarkerDragEnd = (e: any, map: mapboxgl.Map, datacenter: DatacenterV2,
                             markerDragListener, markerDragEndListener) => {
    const coords = e.lngLat;
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    let featureToUpdate = null;
    if (datacenter.auxId) {
      featureToUpdate = featureCollections.features.find(f => f.properties["auxId"] === datacenter.auxId);
    } else {
      featureToUpdate = featureCollections.features.find(f => f.properties["id"] === datacenter.id);
    }
    const isDragging: boolean = featureToUpdate.properties["isDragging"];
    if (isDragging) {
      featureToUpdate.properties["isDragging"] = false;
      source.setData(featureCollections);
      map.dragPan.enable();
      map.getCanvas().style.cursor = 'pointer';
      const datacenterToUpdate: DatacenterV2 = {...featureToUpdate.properties};
      datacenterToUpdate.lat = coords.lat;
      datacenterToUpdate.lon = coords.lng;
      datacenterToUpdate.toUpdate = true;
      this.store.dispatch(
        datacenterActions.datacenterModified({datacenter: datacenterToUpdate})
      );
      // Update AZ position during drag if the datacenter has an availability zone
      if (datacenter.availabilityZoneId) {
        const tempDatacenter = {
          ...datacenter,
          lat: coords.lat,
          lon: coords.lng
        };
        this.availabilityZoneHandler.updateAZPositionInMap(tempDatacenter);
      }
    } else {

    }
    map.off('mousemove', markerDragListener);
    map.off('mouseup', markerDragEndListener);
  }


  private createInfoPopup = (coordinates: mapboxgl.LngLatLike, datacenter: DatacenterV2, map: mapboxgl.Map) => {
    let popupNode = document.createElement('div');
    popupNode.innerHTML = `<div id="angular-popup-host"></div>`;
    const popup = new Popup({closeButton: true, closeOnClick: true})
      .setLngLat(coordinates)
      .setDOMContent(popupNode)
      .addTo(map);
    const hostElement = popupNode.querySelector('#angular-popup-host');
    if (hostElement) {
      const componentRef = createComponent(InfoPopupComponent, {
        hostElement: hostElement,
        environmentInjector: this.applicationRef.injector // Inject ApplicationRef
      });
      componentRef.instance.item = datacenter;
      componentRef.instance.popup = popup;
      this.applicationRef.attachView(componentRef.hostView); // Attach for change detection
      componentRef.changeDetectorRef.detectChanges(); // Manually trigger change detection if needed
    }
  }

  private createClusterInfoPopup = (coordinates: mapboxgl.LngLatLike, datacenter: DatacenterV2, map: mapboxgl.Map) => {

    const popupContent = this.dynamicComponentService.injectComponent(
      ClusterInfoPopupComponent,
      (component) => {component.item = datacenter;}
    );

    this.activePopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
    })
      .setLngLat(coordinates)
      .setDOMContent(popupContent)
      .addTo(map);
  }
}
